import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Service } from './service';

export interface RecordIssue {
  id: number;
  name: string;
  issue: string;
  type: 'missing_year' | 'email_bounce' | 'incomplete_data';
}

export interface RecordTypeData {
  label: string;
  count: number;
  color: string;
}

export interface RecordDistributionResponse {
  distribution: RecordTypeData[];
  totalRecords: number;
}

export interface TimeSeriesData {
  date: string;
  value: number;
}

export interface FinanceData {
  month: string;
  amount: number;
}

export interface FinanceSummaryResponse {
  months: FinanceData[];
  outstandingFees: number;
}

export interface FundraisingProject {
  name: string;
  target: number;
  raised: number;
  percentage: number;
}

export interface EmailCoverage {
  withEmail: number;
  total: number;
  percentage: number;
}

export interface RecentActivityItem {
  id: number;
  title: string;
  message: string;
  timestamp: Date;
  type: 'request' | 'payment' | 'update';
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService extends Service {

  endpoint = this.getEndpoint('dashboard');

  constructor(private http: HttpClient) {
    super();
  }

  getRecordsWithIssues(): Observable<RecordIssue[]> {
    return this.http.get<RecordIssue[]>(this.endpoint + '/data-quality-issues')
      .pipe(catchError(this.handleError('getRecordsWithIssues', [])));
  }

  getRecordTypeDistribution(): Observable<RecordDistributionResponse> {
    return this.http.get<RecordDistributionResponse>(this.endpoint + '/record-distribution')
      .pipe(catchError(this.handleError('getRecordTypeDistribution', { distribution: [], totalRecords: 0 })));
  }

  getPaidUpMembersOverTime(): Observable<TimeSeriesData[]> {
    return this.http.get<TimeSeriesData[]>(this.endpoint + '/paid-members-timeline')
      .pipe(catchError(this.handleError('getPaidUpMembersOverTime', [])));
  }

  getFinanceSummary(): Observable<FinanceSummaryResponse> {
    return this.http.get<FinanceSummaryResponse>(this.endpoint + '/finance-summary')
      .pipe(catchError(this.handleError('getFinanceSummary', { months: [], outstandingFees: 0 })));
  }

  getEmailCoverage(): Observable<EmailCoverage> {
    return this.http.get<EmailCoverage>(this.endpoint + '/email-coverage')
      .pipe(catchError(this.handleError('getEmailCoverage', { withEmail: 0, total: 0, percentage: 0 })));
  }

  getFundraisingProjects(): Observable<FundraisingProject[]> {
    return this.http.get<FundraisingProject[]>(this.endpoint + '/fundraising-projects')
      .pipe(catchError(this.handleError('getFundraisingProjects', [])));
  }

  getRecentActivity(): Observable<RecentActivityItem[]> {
    return this.http.get<RecentActivityItem[]>(this.endpoint + '/recent-activity')
      .pipe(
        map(items => items.map(i => ({ ...i, timestamp: new Date(i.timestamp) }))),
        catchError(this.handleError('getRecentActivity', []))
      );
  }
}
